function isFunction(value) {
  return typeof value === 'function';
}

export class TeamsController {
  constructor({
    view,
    service,
    presenter,
    notify,
    onTeamsUpdated,
    onTeamCreated,
    setLastTeamSelection,
    getLastTeamSelection,
  } = {}) {
    if (!view) {
      throw new Error('TeamsController requires a view');
    }
    if (!service || !isFunction(service.createTeam)) {
      throw new Error('TeamsController requires a service with createTeam');
    }
    if (!presenter || !isFunction(presenter.normalizeCreatePayload)) {
      throw new Error('TeamsController requires a presenter with normalizeCreatePayload');
    }

    this.view = view;
    this.service = service;
    this.presenter = presenter;
    this.onTeamsUpdated = isFunction(onTeamsUpdated) ? onTeamsUpdated : () => {};
    this.onTeamCreated = isFunction(onTeamCreated) ? onTeamCreated : () => {};
    this.setLastTeamSelection = isFunction(setLastTeamSelection) ? setLastTeamSelection : () => {};
    this.getLastTeamSelection = isFunction(getLastTeamSelection) ? getLastTeamSelection : () => null;
    const notifyFallback = isFunction(notify)
      ? notify
      : (message, type, title, duration) => this.view.notify?.(message, type, title, duration);
    this.notify = (message, type = 'info', title, duration) => {
      if (notifyFallback) {
        notifyFallback(message, type, title, duration);
      }
    };
  }

  /** Nothing to bind: teams are created and edited in Settings › Teams & structure. */
  init() {}

  async createTeam(rawInput) {
    return this.#handleCreateTeam(rawInput);
  }


  async uploadTeamLogo(teamId, file) {
    if (!teamId) {
      throw new Error('Team ID is required');
    }
    if (!isFunction(this.service.uploadTeamLogo)) {
      throw new Error('Teams gateway unavailable');
    }
    return this.service.uploadTeamLogo(teamId, file);
  }

  async listTeams({ projectId = null } = {}) {
    if (!isFunction(this.service.listTeams)) {
      return [];
    }
    return this.service.listTeams({ projectId });
  }

  async #handleCreateTeam(rawInput) {
    try {
      const { teamData, logoFile, selection } = this.presenter.normalizeCreatePayload(rawInput || {});
      const created = await this.service.createTeam(teamData);
      if (logoFile && isFunction(this.service.uploadTeamLogo)) {
        try {
          await this.service.uploadTeamLogo(created.id, logoFile);
        } catch (error) {
          // Logo upload failures should not prevent team creation from succeeding.
          this.view.notify?.('Team created, but logo upload failed.', 'warning', 'Teams');
        }
      }

      this.setLastTeamSelection(selection);
      await this.onTeamCreated({ projectId: teamData.projectId ?? null, team: created });
      this.onTeamsUpdated('create', created);
      this.notify(this.presenter.formatCreateSuccess(created), 'success', 'Teams');
      return created;
    } catch (error) {
      this.view.alert?.(error?.message || 'Could not create team');
      throw error;
    }
  }

}

if (typeof window !== 'undefined') {
  window.AgileProControllers = window.AgileProControllers || {};
  window.AgileProControllers.TeamsController = TeamsController;
}
